import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from './Modal';
import { Button } from './Button';
import { Alert } from './Alert';

export interface ConfirmationModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Callback when action is confirmed */
  onConfirm: () => Promise<void> | void;
  /** Modal title */
  title: string;
  /** Confirmation message */
  message: string;
  /** Optional additional description or warnings */
  description?: string;
  /** Confirm button text (defaults to "Confirm") */
  confirmText?: string;
  /** Cancel button text (defaults to "Cancel") */
  cancelText?: string;
  /** Variant of the confirmation (danger for destructive actions) */
  variant?: 'danger' | 'warning' | 'info';
  /** Whether the action is in progress */
  loading?: boolean;
}

/**
 * Confirmation Modal for destructive or important actions
 *
 * Provides a consistent confirmation dialog with proper styling
 * for different action types (danger/warning/info)
 *
 * @example
 * ```tsx
 * <ConfirmationModal
 *   open={showConfirm}
 *   onClose={() => setShowConfirm(false)}
 *   onConfirm={handleDelete}
 *   title="Delete User"
 *   message="Are you sure you want to delete this user?"
 *   description="This action cannot be undone."
 *   variant="danger"
 * />
 * ```
 */
export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  description,
  confirmText,
  cancelText,
  variant = 'danger',
  loading = false,
}) => {
  const { t } = useTranslation('common');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    try {
      setIsProcessing(true);
      setError(null);
      await onConfirm();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsProcessing(false);
    }
  };

  const isLoading = loading || isProcessing;

  // Icon and colors based on variant
  const variantConfig = {
    danger: {
      icon: (
        <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      ),
      iconBg: 'bg-red-100',
      confirmButton: 'danger' as const,
    },
    warning: {
      icon: (
        <svg className="w-6 h-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      ),
      iconBg: 'bg-yellow-100',
      confirmButton: 'primary' as const,
    },
    info: {
      icon: (
        <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
      iconBg: 'bg-blue-100',
      confirmButton: 'primary' as const,
    },
  };

  const config = variantConfig[variant];

  return (
    <Modal open={open} onClose={onClose} title={title} showCloseButton={false} className="max-w-lg">
      <div className="space-y-4">
        {/* Icon */}
        <div className="flex items-center justify-center">
          <div className={`rounded-full p-3 ${config.iconBg}`}>
            {config.icon}
          </div>
        </div>

        {/* Message */}
        <p className="text-center text-gray-800 text-base">{message}</p>

        {/* Description */}
        {description && (
          <p className="text-center text-sm text-gray-600">{description}</p>
        )}

        {/* Error Alert */}
        {error && (
          <Alert variant="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Actions */}
        <div className="flex gap-3 justify-end pt-2">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            {cancelText || t('actions.cancel')}
          </Button>
          <Button
            variant={config.confirmButton}
            onClick={handleConfirm}
            loading={isLoading}
            disabled={isLoading}
          >
            {confirmText || t('actions.confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

ConfirmationModal.displayName = 'ConfirmationModal';
